/**
 * Dialogue Lines Domain
 * Zustand store managing session script audio download state.
 * Triggered when the match session script is identified; downloads audio for the player's language only.
 */

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import i18next from 'i18next';
import {
  fetchManifest,
  preloadSessionScriptAudio,
  resetDialogueLines,
  getCachedManifest,
} from '../services/dialogue-lines/dialogue-lines-service';

// ---------------------------------------------------------------------------
// Session script folder mapping — maps sessionScriptId to its audio folder name
// ---------------------------------------------------------------------------

const SESSION_SCRIPT_FOLDER_MAP: Record<string, string> = {
  'intro-session': '01-session-script-intro-session',
};

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type LoadingState = 'idle' | 'loading-manifest' | 'loading-audio' | 'ready' | 'error';

interface DialogueLinesState {
  loadingState: LoadingState;

  loadSessionScriptAudio: (sessionScriptId: string) => void;
  reset: () => void;
}

// ---------------------------------------------------------------------------
// Store
// ---------------------------------------------------------------------------

const initialState = {
  loadingState: 'idle' as LoadingState,
};

export const useDialogueLinesStore = create<DialogueLinesState>()(
  devtools(
    (set) => ({
      ...initialState,

      loadSessionScriptAudio: (sessionScriptId: string) => {
        const sessionScriptFolder = SESSION_SCRIPT_FOLDER_MAP[sessionScriptId];
        if (!sessionScriptFolder) {
          console.warn(`[DialogueLines] No audio folder mapping for session script: ${sessionScriptId}`);
          return;
        }

        // Skip if already loaded for this session script
        const existing = getCachedManifest();
        if (existing?.sessionScriptFolder === sessionScriptFolder) return;

        const language = i18next.language?.split('-')[0] ?? 'en';

        set({ loadingState: 'loading-manifest' }, false, 'loadSessionScriptAudio/start');

        fetchManifest(sessionScriptFolder)
          .then((manifest) => {
            set({ loadingState: 'loading-audio' }, false, 'loadSessionScriptAudio/manifest-ready');

            return preloadSessionScriptAudio(manifest, language);
          })
          .then(() => {
            set({ loadingState: 'ready' }, false, 'loadSessionScriptAudio/ready');
          })
          .catch((err: unknown) => {
            const message = err instanceof Error ? err.message : String(err);
            console.warn('[DialogueLines] Failed to load audio:', message);
            // Non-fatal: game continues without audio. The message is the log's.
            set({ loadingState: 'error' }, false, 'loadSessionScriptAudio/error');
          });
      },

      reset: () => {
        resetDialogueLines();
        set({ ...initialState }, false, 'reset');
      },
    }),
    { name: 'dialogue-lines-store' },
  ),
);

// ---------------------------------------------------------------------------
// Handlers
// ---------------------------------------------------------------------------

/**
 * The session script is known, from its first step (match-session-script.ts):
 * its voice lines load, in the player's language. A script with no audio
 * folder says so in the console, and the game goes on without them.
 */
export function handleSessionScriptIdentified(sessionScriptId: string): void {
  useDialogueLinesStore.getState().loadSessionScriptAudio(sessionScriptId);
}
